import type { StickyEnlargeTrigger, Trigger } from "@/model/action";
import type { BoardType } from "@/model/baseTypes";
import type { Functionality } from "@/model/stickyType";

import type { AnalyticsData } from "../types";
import { webEvent } from "./event";

const event = (name: string, data?: AnalyticsData) => webEvent("sticky_note", name, data);

/**
 * Triggered after user create a sticky note
 */
export const stickyNoteCreated = (
  functionality: Functionality,
  fromBoard: BoardType,
  trigger: Trigger,
  fromDefault?: boolean,
) => event("created", { functionality, fromBoard, trigger, fromDefault });

/**
 * Triggered when the user opens a chip in the StickyNote
 */
export const stickyNoteAttributeChipClicked = (
  chipName: string,
  functionality: Functionality,
  isStickyActive: boolean,
) => event("attribute_chip_clicked", { functionality, chipName, isStickyActive });

// Triggered when the user opens an item in the action menu of the StickyNote
export const stickyNoteActionMenuItemClicked = (
  itemName: string,
  functionality: Functionality,
  fromMore: boolean,
) => event("action_menu_item_clicked", { functionality, chipName: itemName, fromMore });

// triggered when the user deactivates a StickyNote.
export const stickyNoteDeactivated = (functionality: Functionality) =>
  event("deactivated", { functionality });

// triggered when the user activates a StickyNote.
export const stickyNoteActivated = (functionality: Functionality) =>
  event("activated", { functionality });

// triggered when the user changes the text of a StickyNote. Triggered only once per focus
export const stickyNoteTextChanged = (functionality: Functionality, textLengthBeforeEdit: number) =>
  event("text_changed", { functionality, textLengthBeforeEdit });

// triggered when the user enlarges the StickyNote.
export const stickyNoteEnlarged = (functionality: Functionality, trigger: StickyEnlargeTrigger) =>
  event("enlarged", { functionality, trigger });

// triggered when the user shrinks the StickyNote.
export const stickyNoteShrunk = (functionality: Functionality) =>
  event("shrunk", { functionality });

// Triggered when a bulk action confirmation modal opens (bulk unplan / remove / delete).
export const stickyNoteBulkActionOpened = (
  bulkAction: "unplan" | "remove" | "delete",
  selectedCount: number,
  blockedGroups: string[],
) =>
  event("bulk_action_opened", {
    bulkAction,
    selectedCount,
    blockedGroupCount: blockedGroups.length,
    blockedGroups,
  });

// Triggered when the user confirms a bulk action from the confirmation modal.
export const stickyNoteBulkActionConfirmed = (
  bulkAction: "unplan" | "remove" | "delete",
  selectedCount: number,
  blockedGroups: string[],
) =>
  event("bulk_action_confirmed", {
    bulkAction,
    selectedCount,
    blockedGroupCount: blockedGroups.length,
    blockedGroups,
  });
